import { LoadingButton } from '@mui/lab';
import { Box, Button, Dialog, Stack, Typography } from '@mui/material';
import { CloseIconButton } from '../../components/dialog/CloseIconButton';
import Iconify from '../../components/iconify';
import useResponsive from '../../hooks/useResponsive';

interface LegalNameConfirmModalProps {
  open: boolean;
  legalName: string;
  loading?: boolean;
  forNewHost?: boolean;
  onEdit: VoidFunction;
  onConfirm: VoidFunction;
}

export function LegalNameConfirmModal({
  open,
  legalName,
  loading = false,
  forNewHost = false,
  onEdit,
  onConfirm,
}: LegalNameConfirmModalProps) {
  const isFullScreen = useResponsive('down', 'sm');

  const bodyCopy = forNewHost
    ? 'Please make sure this is their full legal name exactly as it appears on their ID, including first and last name.'
    : 'Please make sure this is your full legal name exactly as it appears on your ID, including first and last name.';

  return (
    <Dialog
      fullScreen={isFullScreen}
      open={open}
      maxWidth="xs"
      fullWidth
      onClose={onEdit}
      PaperProps={{
        sx: {
          borderRadius: isFullScreen ? 0 : 3,
          ...(isFullScreen
            ? {}
            : {
                m: 2,
                boxShadow: (theme) => theme.customShadows.dialog,
              }),
        },
      }}
    >
      <CloseIconButton onClose={onEdit} />
      <Stack direction="row" alignItems="center" spacing={1} p={2}>
        <Iconify icon="fa:drivers-license" width="2rem" sx={{ color: 'primary.main' }} />
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          Confirm Legal Name
        </Typography>
      </Stack>
      <Stack
        spacing={2}
        alignItems="center"
        sx={{
          p: 2,
          ...(isFullScreen
            ? {
                flex: 1,
                justifyContent: 'center',
              }
            : {}),
        }}
      >
        <Box
          sx={{
            width: 1,
            px: 2,
            py: 1.5,
            borderRadius: 1.5,
            bgcolor: 'grey.100',
          }}
        >
          <Typography
            variant="h6"
            sx={{
              textAlign: 'center',
              fontWeight: 700,
              wordBreak: 'break-word',
              color: 'text.primary',
            }}
          >
            {legalName}
          </Typography>
        </Box>

        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ textAlign: 'center', maxWidth: 320 }}
        >
          {bodyCopy}
        </Typography>

        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          spacing={2}
          sx={{ width: 1, pt: 1 }}
        >
          <Button
            variant="text"
            color="primary"
            disabled={loading}
            onClick={onEdit}
            startIcon={<Iconify icon="eva:edit-fill" width={18} />}
          >
            Edit
          </Button>
          <LoadingButton
            variant="contained"
            color="primary"
            loading={loading}
            onClick={onConfirm}
            endIcon={<Iconify icon="eva:arrow-forward-fill" width={18} />}
            sx={{ px: 3, py: 1.25, borderRadius: 1.5, fontWeight: 700 }}
          >
            Confirm
          </LoadingButton>
        </Stack>
      </Stack>
    </Dialog>
  );
}
